import React, { useState, useEffect } from 'react';
import { X, Phone, Mail, MapPin, Send, CheckCircle2, MessageSquare, Sparkles } from 'lucide-react';
import confetti from 'canvas-confetti';

const ContactModal = ({ isOpen, onClose, initialService = '' }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: initialService || 'DGPS Survey (High Accuracy)',
    location: '',
    area: '',
    message: ''
  });

  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (initialService) {
      setFormData((prev) => ({ ...prev, service: initialService }));
    }
  }, [initialService]);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);

    confetti({
      particleCount: 80,
      spread: 70,
      origin: { y: 0.6 }
    });
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="glass-card"
        style={{
          maxWidth: '750px',
          width: '100%',
          maxHeight: '92vh',
          overflowY: 'auto',
          padding: '2.5rem',
          position: 'relative',
          background: '#ffffff',
          border: '1px solid #cbd5e1',
          boxShadow: '0 30px 60px rgba(15, 23, 42, 0.25)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '1.5rem',
            right: '1.5rem',
            background: '#f1f5f9',
            border: '1px solid #cbd5e1',
            color: '#0f172a',
            borderRadius: '50%',
            width: '36px',
            height: '36px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          <X size={20} />
        </button>

        {submitted ? (
          <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
            <div
              style={{
                width: '72px',
                height: '72px',
                borderRadius: '50%',
                background: '#ecfdf5',
                border: '1px solid #a7f3d0',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#059669',
                marginBottom: '1.5rem',
              }}
            >
              <CheckCircle2 size={40} />
            </div>

            <h2 style={{ fontSize: '2rem', marginBottom: '0.75rem', color: '#0f172a' }}>
              Enquiry Received Successfully!
            </h2>

            <p style={{ color: '#475569', fontSize: '1.05rem', maxWidth: '500px', margin: '0 auto 2rem auto', lineHeight: 1.6 }}>
              Thank you <strong style={{ color: '#0f172a' }}>{formData.name}</strong>. B Digital Land Survey & Consulting team will review your requirements and get in touch with you shortly.
            </p>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <a
                href="https://wa.me/919677579748?text=Hello%20B%20Land%20Survey,%20I%20just%20submitted%20a%20quote%20enquiry."
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
                style={{ background: '#25D366', boxShadow: '0 4px 20px rgba(37, 211, 102, 0.4)' }}
              >
                <MessageSquare size={18} /> Chat via WhatsApp Instantly
              </a>
              <button
                onClick={() => {
                  setSubmitted(false);
                  onClose();
                }}
                className="btn-secondary"
              >
                Close Window
              </button>
            </div>
          </div>
        ) : (
          <div>
            {/* Modal Header */}
            <div style={{ marginBottom: '2rem' }}>
              <div className="badge badge-gold" style={{ marginBottom: '0.5rem' }}>
                <Sparkles size={13} /> Accurate Survey, Better Tomorrow
              </div>
              <h2 style={{ fontSize: '1.85rem', color: '#0f172a' }}>Request an Instant Survey Quote</h2>
              <p style={{ color: '#475569', fontSize: '0.95rem', marginTop: '0.35rem' }}>
                B Digital Land Survey & Consulting — Let Your Dreams be our Project.
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              <div className="grid-2" style={{ gap: '1.25rem' }}>
                <div>
                  <label style={{ display: 'block', color: '#0f172a', fontSize: '0.875rem', fontWeight: '700', marginBottom: '0.4rem' }}>
                    Full Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g. Bagavathi Raja"
                    style={{
                      width: '100%',
                      padding: '0.8rem 1rem',
                      borderRadius: '8px',
                      background: '#f8fafc',
                      border: '1px solid #cbd5e1',
                      color: '#0f172a',
                      fontSize: '0.9rem',
                      outline: 'none',
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', color: '#0f172a', fontSize: '0.875rem', fontWeight: '700', marginBottom: '0.4rem' }}>
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+91 96775 79748"
                    style={{
                      width: '100%',
                      padding: '0.8rem 1rem',
                      borderRadius: '8px',
                      background: '#f8fafc',
                      border: '1px solid #cbd5e1',
                      color: '#0f172a',
                      fontSize: '0.9rem',
                      outline: 'none',
                    }}
                  />
                </div>
              </div>

              <div className="grid-2" style={{ gap: '1.25rem' }}>
                <div>
                  <label style={{ display: 'block', color: '#0f172a', fontSize: '0.875rem', fontWeight: '700', marginBottom: '0.4rem' }}>
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="bagavathiraja96@gmail.com"
                    style={{
                      width: '100%',
                      padding: '0.8rem 1rem',
                      borderRadius: '8px',
                      background: '#f8fafc',
                      border: '1px solid #cbd5e1',
                      color: '#0f172a',
                      fontSize: '0.9rem',
                      outline: 'none',
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', color: '#0f172a', fontSize: '0.875rem', fontWeight: '700', marginBottom: '0.4rem' }}>
                    Survey Work Type *
                  </label>
                  <select
                    value={formData.service}
                    onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.8rem 1rem',
                      borderRadius: '8px',
                      background: '#f8fafc',
                      border: '1px solid #cbd5e1',
                      color: '#0f172a',
                      fontSize: '0.9rem',
                      outline: 'none',
                    }}
                  >
                    <option value="DGPS Survey (High Accuracy)">DGPS Survey (High Accuracy)</option>
                    <option value="Boundary Survey (Land Details)">Boundary Survey (Land Details)</option>
                    <option value="Water Resources Development (W.R.D. Work)">Water Resources Development (W.R.D. Work)</option>
                    <option value="Highways, Bypass Road Alignment & LPS Schedule">Highways, Bypass Road Alignment & LPS Schedule</option>
                    <option value="Topographical & Contour Survey">Topographical & Contour Survey</option>
                  </select>
                </div>
              </div>

              <div className="grid-2" style={{ gap: '1.25rem' }}>
                <div>
                  <label style={{ display: 'block', color: '#0f172a', fontSize: '0.875rem', fontWeight: '700', marginBottom: '0.4rem' }}>
                    Site Location / District *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="e.g. Theni / Uttamapalayam / Madurai"
                    style={{
                      width: '100%',
                      padding: '0.8rem 1rem',
                      borderRadius: '8px',
                      background: '#f8fafc',
                      border: '1px solid #cbd5e1',
                      color: '#0f172a',
                      fontSize: '0.9rem',
                      outline: 'none',
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', color: '#0f172a', fontSize: '0.875rem', fontWeight: '700', marginBottom: '0.4rem' }}>
                    Estimated Land Extent
                  </label>
                  <input
                    type="text"
                    value={formData.area}
                    onChange={(e) => setFormData({ ...formData, area: e.target.value })}
                    placeholder="e.g. 5 Acres / 10 Grounds"
                    style={{
                      width: '100%',
                      padding: '0.8rem 1rem',
                      borderRadius: '8px',
                      background: '#f8fafc',
                      border: '1px solid #cbd5e1',
                      color: '#0f172a',
                      fontSize: '0.9rem',
                      outline: 'none',
                    }}
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', color: '#0f172a', fontSize: '0.875rem', fontWeight: '700', marginBottom: '0.4rem' }}>
                  Project Notes / Specific Requirements
                </label>
                <textarea
                  rows={3}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Describe your site details or W.R.D / DGPS survey requirements..."
                  style={{
                    width: '100%',
                    padding: '0.8rem 1rem',
                    borderRadius: '8px',
                    background: '#f8fafc',
                    border: '1px solid #cbd5e1',
                    color: '#0f172a',
                    fontSize: '0.9rem',
                    outline: 'none',
                    fontFamily: 'var(--font-body)',
                  }}
                />
              </div>

              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '0.75rem', marginTop: '0.75rem', marginBottom: '0.75rem', fontSize: '0.8rem', color: '#475569' }}>
                <div style={{ fontWeight: '700', color: '#0f172a', marginBottom: '0.2rem' }}>Survey Calculation Rate:</div>
                <div>• Base Fee: <strong>₹15,000</strong></div>
                <div>• Area Rate: <strong>+ ₹3,000 for every acre</strong></div>
                <div style={{ color: '#dc2626', fontWeight: '600', marginTop: '0.2rem', fontStyle: 'italic' }}>
                  *Note: "Payment may change depending on the location."
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="btn-primary btn-glow"
                style={{ width: '100%', justifyContent: 'center', marginTop: '0.25rem' }}
              >
                Submit Survey Proposal Request <Send size={16} />
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactModal;
